import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  DocumentationLinkLinkableTypeEnum,
  SiteArea,
  useEdmDocumentationsIdsQuery,
} from 'graphql-common';
import { useEdmDocumentationsLazyQuery } from '@lib/graphql-common';
import _get from 'lodash/get';
import SiteAreas from 'components/SiteAreas/SiteAreas';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import { Option, Values } from '@lib/interfaces/form';
import Modal from '@lib/components/Modal/Modal';
import { SubmitAction } from '@lib/enums/form';
import AreaCreateForm from 'components/SiteArea/AreaCreateForm/AreaCreateForm';
import SiteAreaViewEditModal from 'components/SiteArea/SiteAreaViewEditModal/SiteAreaViewEditModal';
import DocumentsModalTableMultiselect from 'components/DocumentsModalTableMultiselect/DocumentsModalTableMultiselect';
import { FormRef } from '@lib/components/ReactHookForm/FormContainer';
import GetQrCodeForm from 'components/GetQrCodeForm/GetQrCodeForm';
import { useTableItemsBulkSelect } from '@lib/hooks/useTableItemsBulkSelect';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { IconName } from '@lib/components/Modal/enums';
import Trans from '@lib/components/Trans/Trans';
import useLibModalManagement from '@lib/hooks/useModalManagement';
import { SiteFieldNames } from '@lib/enums/fieldNames/siteFieldNames';
import { GetLoadOptions } from '@lib/components/Select/types';
import { getDocumentationsLoadOptions as getDocumentationsLoadOptionsFn } from 'utils/fetch/searchLists/documentationsSearchList';
import { getGraphQLErrors } from '@lib/utils/graphQLErrors';
import useSiteAreasModalManagement from './utils/useSiteAreasModalManagement';
import useSiteAreasActions from './utils/useSiteAreasActions';
import getSiteAreasActionsForSelected from './utils/getSiteAreasActionsForSelected';
import getSiteAreasActionsMenu from './utils/getSiteAreasActionsMenu';
import getSiteAreasColumns from './utils/getSiteAreasColumns';

const SELECT_DOCUMENTS_MODAL = 'select-documents';

function SiteAreasContainer() {
  const { t } = useTranslation();
  const submitNewAreaRef = useRef<SubmitAction>(undefined);
  const formNewAreaRef = useRef<FormRef>(undefined);
  const formEditAreaRef = useRef<FormRef>(undefined);
  const initialDocumentationLinksRef = useRef<Option[]>([]);
  const { id: siteId = '' } = useParams();
  const { pathname } = useLocation();

  const { getUserAccessByComponent, getUserAccessByCompany }: ContextProps =
    useOutletContext();
  const isViewOnly =
    getUserAccessByComponent(AccessComponentEnum.SitesManagement) ===
    AccessLevelEnum.Read;
  const isDocumentationPlanLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;
  // Combines plan-level (EDM disabled for the company) and component-level
  // (user role has no EDM access) locks — either one should hide the
  // documents field entirely and skip fetching linked documentation.
  const isDocumentationLocked =
    isDocumentationPlanLocked ||
    getUserAccessByComponent(AccessComponentEnum.DocumentationManagement) ===
      AccessLevelEnum.LimitedRead;

  const useBulkSelectResult = useTableItemsBulkSelect<SiteArea>();
  const { getBulkSelectedItemsCount, getSelectedItemsCount } =
    useBulkSelectResult;

  const useModalManagementResult = useSiteAreasModalManagement({
    submitNewAreaRef,
    useBulkSelectResult,
  });
  const {
    closeGetQrCodeModal,
    closeSiteAreaCreateModal,
    closeSiteAreaDeleteModal,
    closeSiteAreaModal,
    isGetQrCodeModalOpened,
    isSiteAreaCreateModalOpened,
    isSiteAreaDeleteModalOpened,
    isSiteAreaEditModalOpened,
    isSiteAreaViewModalOpened,
    openSiteAreaCreateModal,
    openSiteAreaEditModal,
    openSiteAreaViewModal,
  } = useModalManagementResult;

  const {
    siteAreasQueryHookResult,
    createSiteAreaLoading,
    createSiteAreaReset,
    destroyLoading,
    getQrCode,
    getQrCodesForSelected,
    getSiteMembershipsRecipientsLoadOptions,
    graphQLErrors,
    onBulkDelete,
    onConfirmedDelete,
    onCreate,
    onDelete,
    onEditHandler,
    onQrCodeCreate,
    onUpdateHandler,
    onViewHandler,
    siteAreaData,
    siteAreaError,
    siteData,
    siteError,
    siteLoading,
    updateSiteAreaMutationLoading,
    updateSiteAreaReset,
    qrCodeCreateMutationResult,
  } = useSiteAreasActions({
    formNewAreaRef,
    initialDocumentationLinksRef,
    siteId,
    submitNewAreaRef,
    useModalManagementResult,
    useBulkSelectResult,
  });

  const selectedItemsCount = getSelectedItemsCount(
    siteAreasQueryHookResult.totalCount,
  );
  const bulkSelectedItemsCount = getBulkSelectedItemsCount(
    siteAreasQueryHookResult.totalCount,
  );

  const tableColumns = getSiteAreasColumns({ getQrCode, onViewHandler, t });
  const tableActionsMenu = getSiteAreasActionsMenu({
    onViewHandler,
    onEditHandler,
    onDelete,
    t,
    isViewOnly,
  });
  const tableActionsForSelected = getSiteAreasActionsForSelected({
    getQrCodesForSelected,
    onBulkDelete,
    selectedItemsCount,
    t,
    isViewOnly,
  });

  // Documents modal
  const [fetchDocumentationsLazyQuery] = useEdmDocumentationsLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const getDocumentationsLoadOptions = useCallback<GetLoadOptions>(
    () => getDocumentationsLoadOptionsFn(fetchDocumentationsLazyQuery),
    [fetchDocumentationsLazyQuery],
  );

  const useDocumentsModalResult = useLibModalManagement([
    SELECT_DOCUMENTS_MODAL,
  ]);

  const openDocumentsModal = useCallback(() => {
    useDocumentsModalResult.openModal(SELECT_DOCUMENTS_MODAL);
  }, [useDocumentsModalResult]);

  const onCloseDocumentsModal = useCallback(() => {
    useDocumentsModalResult.closeModal(SELECT_DOCUMENTS_MODAL);
  }, [useDocumentsModalResult]);

  const activeFormRef = isSiteAreaCreateModalOpened
    ? formNewAreaRef
    : formEditAreaRef;

  const onDocumentsSelect = useCallback(
    (values: string[], visibilityMap?: Record<string, boolean>) => {
      if (activeFormRef?.current) {
        activeFormRef.current.setValue(
          SiteFieldNames.DocumentationLinks,
          values.length
            ? values.map((docId) => {
                const visibility = visibilityMap?.[docId];
                return visibility === undefined
                  ? { value: docId }
                  : { value: docId, visibleViaQr: visibility };
              })
            : [],
          { shouldDirty: true, shouldTouch: true, shouldValidate: true },
        );
      }
    },
    [activeFormRef],
  );

  const isDocumentsModalOpen = useDocumentsModalResult.isModalOpened(
    SELECT_DOCUMENTS_MODAL,
  );

  const documentationsInitialVisibilityMap = useMemo(() => {
    if (!isDocumentsModalOpen) return undefined;
    const list = activeFormRef?.current?.values?.[
      SiteFieldNames.DocumentationLinks
    ] as Array<{ value?: string; visibleViaQr?: boolean }> | undefined;
    if (!Array.isArray(list)) return {};
    return list.reduce<Record<string, boolean>>((acc, item) => {
      const id = String(item?.value ?? '');
      if (!id) return acc;
      if (item?.visibleViaQr === undefined) return acc;
      acc[id] = item.visibleViaQr;
      return acc;
    }, {});
  }, [isDocumentsModalOpen, activeFormRef]);

  // Prefill documentationLinks for area edit. Rows carry only `{ value: id }`;
  // the visibility baseline is loaded lazily by DocumentsModalTableMultiselect
  // on open.
  const editAreaId = siteAreaData?.id;
  const {
    data: linkedDocsData,
    loading: linkedDocsLoading,
    error: linkedDocsError,
  } = useEdmDocumentationsIdsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      onlyIds: true,
      filters: [
        {
          linkedAreasId: {
            predicate: { eq: editAreaId || '' },
          },
        },
      ],
    },
    skip: !editAreaId || !isSiteAreaEditModalOpened || isDocumentationLocked,
  });

  const linkedDocumentationLinks: Option[] = useMemo(() => {
    const ids = _get(linkedDocsData, 'data.collection', []) as Array<{
      id: string;
    }>;
    return ids.map(({ id }) => ({ value: id }));
  }, [linkedDocsData]);

  useEffect(() => {
    if (!isSiteAreaEditModalOpened || !editAreaId) {
      initialDocumentationLinksRef.current = [];
      return;
    }
    if (!linkedDocsData) return;
    initialDocumentationLinksRef.current = linkedDocumentationLinks;
    if (formEditAreaRef.current) {
      formEditAreaRef.current.setValue(
        SiteFieldNames.DocumentationLinks,
        linkedDocumentationLinks,
        { shouldDirty: false, shouldTouch: false, shouldValidate: false },
      );
    }
  }, [
    editAreaId,
    isSiteAreaEditModalOpened,
    linkedDocsData,
    linkedDocumentationLinks,
  ]);

  const areaDataWithDocs = useMemo(
    () =>
      siteAreaData
        ? {
            ...siteAreaData,
            [SiteFieldNames.DocumentationLinks]: linkedDocumentationLinks,
          }
        : siteAreaData,
    [siteAreaData, linkedDocumentationLinks],
  );

  const isAreaPrefillLoading =
    isSiteAreaEditModalOpened &&
    Boolean(editAreaId) &&
    !isDocumentationLocked &&
    (linkedDocsLoading || (!linkedDocsData && !linkedDocsError));

  const toggleSiteAreaModal = () => {
    if (isSiteAreaEditModalOpened) {
      openSiteAreaViewModal();
    } else if (isSiteAreaViewModalOpened) {
      openSiteAreaEditModal();
    }
  };

  useEffect(() => {
    if (!isSiteAreaCreateModalOpened) {
      createSiteAreaReset();
    }
    if (!isSiteAreaEditModalOpened) {
      updateSiteAreaReset();
    }
  }, [
    createSiteAreaReset,
    isSiteAreaCreateModalOpened,
    isSiteAreaEditModalOpened,
    updateSiteAreaReset,
  ]);

  return (
    <>
      <BreadCrumbs
        data={getBreadCrumbsDataByKey({
          pathname,
          t,
          entity: siteData as Values,
        })}
      />
      <SiteAreas
        siteAreasQueryHookResult={siteAreasQueryHookResult}
        openSiteAreaCreateModal={openSiteAreaCreateModal}
        siteAreaError={siteAreaError}
        siteData={siteData}
        siteError={siteError}
        siteLoading={siteLoading}
        tableActionsForSelected={tableActionsForSelected}
        tableActionsMenu={tableActionsMenu}
        tableColumns={tableColumns}
        useBulkSelectResult={useBulkSelectResult}
        isViewOnly={isViewOnly}
      />
      <SiteAreaViewEditModal
        areaData={areaDataWithDocs as SiteArea}
        formRef={formEditAreaRef}
        getDocumentationsLoadOptions={
          isDocumentationLocked ? undefined : getDocumentationsLoadOptions
        }
        graphQLErrors={graphQLErrors}
        isDocumentationLocked={isDocumentationLocked}
        isEditMode={isSiteAreaEditModalOpened}
        isOpen={isSiteAreaViewModalOpened || isSiteAreaEditModalOpened}
        loading={updateSiteAreaMutationLoading || isAreaPrefillLoading}
        onClose={closeSiteAreaModal}
        onSubmit={onUpdateHandler}
        onToggleMode={toggleSiteAreaModal}
        openDocumentsModal={
          isDocumentationLocked ? undefined : openDocumentsModal
        }
      />
      <Modal
        isOpen={isSiteAreaCreateModalOpened}
        onClose={closeSiteAreaCreateModal}
        title={t('create-new-site-area')}
        titleAlign="left"
      >
        <AreaCreateForm
          formNewAreaRef={formNewAreaRef}
          getDocumentationsLoadOptions={
            isDocumentationLocked ? undefined : getDocumentationsLoadOptions
          }
          graphQLErrors={graphQLErrors}
          loading={createSiteAreaLoading}
          onCancel={closeSiteAreaCreateModal}
          onSubmit={onCreate}
          openDocumentsModal={
            isDocumentationLocked ? undefined : openDocumentsModal
          }
          submitNewAreaRef={submitNewAreaRef}
        />
      </Modal>
      <Modal
        isOpen={isGetQrCodeModalOpened}
        onClose={closeGetQrCodeModal}
        title={
          bulkSelectedItemsCount > 1
            ? t('get-area-qr-codes')
            : t('get-area-qr-code')
        }
      >
        <GetQrCodeForm
          getRecipientsLoadOptions={getSiteMembershipsRecipientsLoadOptions}
          graphQLErrors={getGraphQLErrors(qrCodeCreateMutationResult?.error)}
          loading={!!qrCodeCreateMutationResult?.loading}
          onCancel={closeGetQrCodeModal}
          onSubmit={onQrCodeCreate}
          selectedItemsCount={selectedItemsCount}
        />
      </Modal>
      <DocumentsModalTableMultiselect
        isOpen={isDocumentsModalOpen}
        onClose={onCloseDocumentsModal}
        onSelect={onDocumentsSelect}
        formRef={activeFormRef}
        fieldName={SiteFieldNames.DocumentationLinks}
        linkableId={editAreaId}
        linkableType={DocumentationLinkLinkableTypeEnum.SiteArea}
        visibleViaQr={
          isDocumentationPlanLocked
            ? undefined
            : { initialVisibilityMap: documentationsInitialVisibilityMap }
        }
      />
      <ConfirmationModal
        cancelButtonLabel={t('cancel')}
        confirmButtonLabel={t('yes-delete')}
        description={
          <Trans
            i18nKey={
              bulkSelectedItemsCount > 1
                ? 'delete-site-areas-text'
                : 'delete-site-area-text'
            }
            values={{ count: bulkSelectedItemsCount }}
          />
        }
        icon={IconName.Delete}
        isOpen={isSiteAreaDeleteModalOpened}
        loading={destroyLoading}
        onClose={closeSiteAreaDeleteModal}
        onConfirm={onConfirmedDelete}
        title={t('are-you-sure')}
        useProgress
      />
    </>
  );
}

export default SiteAreasContainer;
